import React, { useState, useEffect, useCallback, useMemo } from "react";
import { FaClone, FaFile } from "react-icons/fa";
import promptFragmentsService from "../../services/promptFragmentsService";
import ContextMenu from "../common/ContextMenu";
import FragmentListItem from "../fragments/FragmentListItem";
import FragmentSearch from "../fragments/FragmentSearch";
import FragmentDetailsModal from "../fragments/FragmentDetailsModal";

const PromptFragmentsTab = ({
  currentPromptFragments,
  editor,
  onFragmentAdded,
}) => {
  const [fragments, setFragments] = useState([]);
  const [selectedFragment, setSelectedFragment] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [fragmentsInUse, setFragmentsInUse] = useState({});
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [fragmentVersions, setFragmentVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [versionInUse, setVersionInUse] = useState(null);
  const [selectedVersionContent, setSelectedVersionContent] = useState("");
  const [latestContent, setLatestContent] = useState("");
  const [viewingHistorical, setViewingHistorical] = useState(false);
  const [showDiff, setShowDiff] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const [rightClickedFragment, setRightClickedFragment] = useState(null);

  const fetchFragments = useCallback(async () => {
    setLoading(true);
    try {
      const fragmentsData = await promptFragmentsService.getFragments();

      const fragmentsWithLatestVersions = await Promise.all(
        fragmentsData.map(async (fragment) => {
          try {
            const latestVersion =
              await promptFragmentsService.getLatestFragmentVersion(
                fragment.id
              );
            return {
              ...fragment,
              latest: latestVersion,
            };
          } catch (err) {
            console.error(
              `Failed to fetch latest version for fragment ${fragment.id}:`,
              err
            );
            return {
              ...fragment,
              latest: null,
            };
          }
        })
      );

      setFragments(fragmentsWithLatestVersions);
    } catch (err) {
      console.error("Failed to fetch fragments:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFragments();
  }, [fetchFragments]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const sortFragments = useCallback(
    (fragmentList) => {
      if (!Array.isArray(fragmentList)) return [];
      return [...fragmentList].sort((a, b) => {
        const aInUse = Boolean(fragmentsInUse[a.id]);
        const bInUse = Boolean(fragmentsInUse[b.id]);
        if (aInUse && !bInUse) return -1;
        if (!aInUse && bInUse) return 1;
        return [...fragmentList].sort((a, b) => a.name.localeCompare(b.name));
      });
    },
    [fragmentsInUse]
  );

  useEffect(() => {
    const lookup = {};
    if (currentPromptFragments && currentPromptFragments.length > 0) {
      currentPromptFragments.forEach((fragment) => {
        lookup[fragment.prompt_fragment_id] = fragment.version;
      });
    }
    setFragmentsInUse(lookup);
  }, [currentPromptFragments]);

  const filteredFragments = useMemo(() => {
    if (!Array.isArray(fragments)) {
      return [];
    }

    let filtered = fragments;
    if (searchTerm) {
      filtered = fragments.filter(
        (fragment) =>
          fragment.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (fragment.description &&
            fragment.description
              .toLowerCase()
              .includes(searchTerm.toLowerCase()))
      );
    }

    return sortFragments(filtered);
  }, [searchTerm, fragments, sortFragments]);

  const openFragmentInformation = async (fragment) => {
    setSelectedFragment(fragment);
    await fetchFragmentVersions(fragment.id);

    setShowModal(true);
  };

  const closeModal = useCallback(() => {
    setShowModal(false);
    setSelectedFragment(null);
    setFragmentVersions([]);
    setSelectedVersion(null);
    setSelectedVersionContent("");
    setLatestContent("");
    setViewingHistorical(false);
    setShowDiff(false);
  }, [
    setShowModal,
    setSelectedFragment,
    setFragmentVersions,
    setSelectedVersion,
    setSelectedVersionContent,
    setLatestContent,
    setViewingHistorical,
    setShowDiff,
  ]);

  const fetchFragmentVersions = async (fragmentId) => {
    setLoadingVersions(true);
    try {
      const versions = await promptFragmentsService.getFragmentVersionsById(
        fragmentId
      );
      setFragmentVersions(versions);

      if (versions.length > 0) {
        let foundVersion;

        if (fragmentsInUse[fragmentId]) {
          foundVersion = versions.find(
            (version) => version.version === fragmentsInUse[fragmentId]
          );
        }
        const selectedVersionId = fragmentsInUse[fragmentId]
          ? foundVersion.id
          : versions[0].id;
        setSelectedVersion(selectedVersionId);
        setVersionInUse(selectedVersionId);

        await loadVersionContent(selectedVersionId, false, versions);
        setLatestContent(versions[0].content || "");
      }
    } catch (err) {
    } finally {
      setLoadingVersions(false);
    }
  };

  const handleRightClick = (e, fragment) => {
    e.preventDefault();
    setSelectedFragment(fragment);
    setRightClickedFragment(fragment);
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      items: [
        {
          label: "View Details",
          icon: FaFile,
          onClick: () => openFragmentInformation(fragment),
        },
        {
          label: "Add to Prompt",
          icon: FaClone,
          onClick: () => addFragmentToPrompt(fragment),
        },
      ],
    });
  };

  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
  }, [setContextMenu]);

  const loadVersionContent = useCallback(
    async (versionId, isHistorical, fetchedVersions = fragmentVersions) => {
      try {
        setLoadingVersions(true);
        const versionData = fetchedVersions.find((v) => v.id === versionId);
        if (versionData) {
          setSelectedVersionContent(versionData.content || "");
        }

        const isCurrentVersionInUse =
          fragmentsInUse &&
          selectedFragment &&
          fragmentsInUse[selectedFragment.id] === versionData?.version;

        if (isHistorical) {
          if (!latestContent) {
            const latestVersionData = fetchedVersions.find(
              (v) => v.id === fetchedVersions[0].id
            );
            setLatestContent(latestVersionData?.content || "");
          }

          setShowDiff(!isCurrentVersionInUse);
        } else {
          setShowDiff(false);
        }
      } catch (error) {
        console.error(`Error loading version ${versionId}:`, error);
      } finally {
        setLoadingVersions(false);
      }
    },
    [fragmentVersions, latestContent, selectedFragment, fragmentsInUse]
  );

  const handleVersionSelect = useCallback(
    async (versionId) => {
      try {
        setLoadingVersions(true);
        const isHistorical = versionId !== versionInUse;
        setViewingHistorical(isHistorical);
        setSelectedVersion(versionId);
        await loadVersionContent(versionId, isHistorical);
      } catch (error) {
        console.error("Error selecting version:", error);
      } finally {
        setLoadingVersions(false);
      }
    },
    [versionInUse, loadVersionContent]
  );

  const getComparisonContent = useCallback(() => {
    if (!selectedFragment || !fragmentsInUse) return latestContent;

    const currentVersionInUse = fragmentsInUse[selectedFragment.id];

    if (currentVersionInUse) {
      const versionInUseData = fragmentVersions.find(
        (v) =>
          fragmentVersions.length -
            fragmentVersions.findIndex((fv) => fv.id === v.id) ===
          currentVersionInUse
      );
      return versionInUseData?.content || latestContent;
    }

    return latestContent;
  }, [selectedFragment, fragmentsInUse, fragmentVersions, latestContent]);

  const getComparisonVersionNumber = useCallback(() => {
    if (!selectedFragment || !fragmentsInUse) return "Latest";

    const currentVersionInUse = fragmentsInUse[selectedFragment.id];
    return currentVersionInUse ? currentVersionInUse : "Latest";
  }, [selectedFragment, fragmentsInUse]);

  const getSelectedVersionNumber = useCallback(() => {
    if (!selectedVersion || !fragmentVersions.length) return "";

    const index = fragmentVersions.findIndex((v) => v.id === selectedVersion);
    return fragmentVersions.length - index;
  }, [selectedVersion, fragmentVersions]);

  const getAddButtonText = useCallback(
    (fragment, selectedVersionNum) => {
      if (!fragment || !fragmentsInUse) return "Add Fragment to Prompt";

      const currentVersionInUse = fragmentsInUse[fragment.id];
      if (!currentVersionInUse) return "Add Fragment to Prompt";

      const selectedVersionNumber =
        typeof selectedVersionNum === "string"
          ? parseInt(selectedVersionNum.replace(/\D/g, ""), 10)
          : selectedVersionNum;

      if (selectedVersionNumber === currentVersionInUse) {
        return "Add Fragment to Prompt";
      } else if (selectedVersionNumber < currentVersionInUse) {
        return "Downgrade Fragment Version";
      } else {
        return "Upgrade Fragment Version";
      }
    },
    [fragmentsInUse]
  );

  const addFragmentToPrompt = useCallback(
    (targetFragment, versionNumberInput) => {
      if (!editor || !targetFragment) return;

      const fragment = targetFragment || rightClickedFragment;
      if (!fragment) return;

      const contentText = `{% include "${fragment.block_name}" %}`;
      const editorInstance = editor.current?.editor;

      let versionNumber;
      if (
        typeof versionNumberInput === "string" &&
        versionNumberInput.startsWith("v")
      ) {
        versionNumber = parseInt(versionNumberInput.substring(1), 10);
      } else {
        versionNumber = versionNumberInput;
      }

      let fragmentVersion = versionNumber || fragment.version;

      if (!fragmentVersion && fragment.id in fragmentsInUse) {
        fragmentVersion = fragmentsInUse[fragment.id];
      } else if (!fragmentVersion) {
        const foundFragment = fragments.find((f) => f.id === fragment.id);
        fragmentVersion = foundFragment ? foundFragment.latest : null;
      }

      const fragmentReference = {
        block_name: fragment.block_name,
        prompt_fragment_id: fragment.id,
        version: fragmentVersion,
        is_fragment_changed: 1,
      };

      const currentVersionInUse = fragmentsInUse[fragment.id];
      const shouldAddNewText =
        !currentVersionInUse || currentVersionInUse === fragmentVersion;

      if (editorInstance && shouldAddNewText) {
        const selection = editorInstance.getSelection();
        const position = selection ? selection.getPosition() : null;

        if (position) {
          editorInstance.executeEdits("", [
            {
              range: {
                startLineNumber: position.lineNumber,
                startColumn: position.column,
                endLineNumber: position.lineNumber,
                endColumn: position.column,
              },
              text: contentText,
            },
          ]);
          editorInstance.focus();
        }
      }

      onFragmentAdded(fragmentReference, shouldAddNewText ? contentText : null);
      closeContextMenu();
      if (showModal) closeModal();
    },
    [
      editor,
      rightClickedFragment,
      fragmentsInUse,
      fragments,
      onFragmentAdded,
      showModal,
      closeContextMenu,
      closeModal,
    ]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="p-2 border-b border-gray-700">
        <FragmentSearch searchTerm={searchTerm} onChange={handleSearch} />
      </div>

      <div className="flex-grow overflow-y-auto dark-slim-scrollbar">
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
          </div>
        ) : !filteredFragments || filteredFragments.length === 0 ? (
          <div className="p-4 text-gray-400">
            No fragments match your search criteria.
          </div>
        ) : (
          <div>
            {filteredFragments.map((fragment) => {
              const isInUse = fragmentsInUse[fragment.id];
              const currentVersion = isInUse
                ? fragmentsInUse[fragment.id]
                : null;
              const latestVersion = fragment.latest;
              const needsUpdate = isInUse && currentVersion < latestVersion;

              return (
                <FragmentListItem
                  key={fragment.id}
                  fragment={fragment}
                  isSelected={selectedFragment?.id === fragment.id}
                  isInUse={isInUse}
                  needsUpdate={needsUpdate}
                  currentPromptFragments={currentPromptFragments}
                  onClick={openFragmentInformation}
                  onContextMenu={handleRightClick}
                />
              );
            })}
          </div>
        )}
      </div>

      {showModal && (
        <FragmentDetailsModal
          fragment={selectedFragment}
          onClose={closeModal}
          loadingVersions={loadingVersions}
          fragmentVersions={fragmentVersions}
          selectedVersion={selectedVersion}
          onVersionSelect={handleVersionSelect}
          fragmentsInUse={fragmentsInUse}
          viewingHistorical={viewingHistorical}
          showDiff={showDiff}
          setShowDiff={setShowDiff}
          selectedVersionContent={selectedVersionContent}
          getComparisonContent={getComparisonContent}
          getComparisonVersionNumber={getComparisonVersionNumber}
          getSelectedVersionNumber={getSelectedVersionNumber}
          getAddButtonText={getAddButtonText}
          onAddFragmentToPrompt={addFragmentToPrompt}
        />
      )}

      {contextMenu && (
        <ContextMenu
          items={contextMenu.items}
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={closeContextMenu}
        />
      )}
    </div>
  );
};

export default PromptFragmentsTab;
